import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { submitAgencyInquiry } from "@/lib/checkout.functions";

const labelCls = "text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground";
const inputCls =
  "mt-1.5 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground focus:border-foreground focus:outline-none";

export function AgencyForm({ onBack }: { onBack: () => void }) {
  const submit = useServerFn(submitAgencyInquiry);
  const [state, setState] = useState({
    full_name: "",
    email: "",
    company: "",
    website: "",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  if (status === "sent") {
    return (
      <div className="rounded-2xl border border-border bg-card/40 p-8 text-center">
        <p
          className="text-[10px] font-medium uppercase tracking-[0.18em]"
          style={{ color: "#C8A84B" }}
        >
          Got it
        </p>
        <h3 className="mt-3 font-display text-2xl font-semibold text-foreground">
          Thanks, we'll be in touch within 1 business day.
        </h3>
        <p className="mt-3 text-sm text-muted-foreground">
          A Shugert team member will email you with white-label pricing and a short scoping call invite.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setStatus("sending");
        setError(null);
        try {
          const res = await submit({ data: state });
          if ("error" in res) throw new Error(res.error);
          setStatus("sent");
        } catch (err) {
          setStatus("error");
          setError(err instanceof Error ? err.message : "Something went wrong");
        }
      }}
      className="grid gap-5"
    >
      <p className="text-sm text-muted-foreground">
        Tell us about your agency and we'll get back with white-label pricing and onboarding details.
      </p>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="ag_name">Full name *</label>
          <input id="ag_name" required value={state.full_name} onChange={(e) => setState({ ...state, full_name: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="ag_email">Email *</label>
          <input id="ag_email" type="email" required value={state.email} onChange={(e) => setState({ ...state, email: e.target.value })} className={inputCls} />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="ag_company">Agency name</label>
          <input id="ag_company" value={state.company} onChange={(e) => setState({ ...state, company: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="ag_website">Agency website</label>
          <input id="ag_website" value={state.website} onChange={(e) => setState({ ...state, website: e.target.value })} className={inputCls} />
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor="ag_msg">How can we help?</label>
        <textarea id="ag_msg" rows={4} value={state.message} onChange={(e) => setState({ ...state, message: e.target.value })} className={inputCls} />
      </div>
      {error && (
        <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}
      <div className="flex items-center justify-between gap-3 pt-2">
        <button type="button" onClick={onBack} className="rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground hover:bg-card">
          ← Back
        </button>
        <button type="submit" disabled={status === "sending"} className="rounded-full bg-foreground px-6 py-2.5 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50">
          {status === "sending" ? "Sending…" : "Request pricing →"}
        </button>
      </div>
    </form>
  );
}